/**
 * Shared demo-mode flag (control-panel-spec §3 + design-language-modernization
 * 2026-06-07).
 *
 * Spy-movie flourishes — helicopter rotor spin, focus glow, reticles, extra
 * motion — all ride THIS one flag, as a single coordinated toggle: dialed up for
 * sales/demos, restrained for government operational trials. Anything that is
 * polish-over-function lives behind it.
 *
 * In OPERATIONAL mode (default, flag OFF) the COP stays within the Law-6 motion
 * budget — the only moving things are the live camera feeds and the red anomaly
 * pulses. No baked rotor spin, no decorative animation.
 *
 * Source of truth (resolved live, evaluated in order; default OFF):
 *   1. `?demo=1` query param (`demo=on` / `demo=true` also count; `demo=0` /
 *      `demo=false` force it OFF) — a shareable demo URL.
 *   2. `localStorage['tsr.demoMode'] === '1'` — a sticky per-browser toggle.
 *
 * Kept deliberately tiny and dependency-free so every flourish can import it
 * without pulling in React/prefs state — it is read at entity-build time, not per
 * frame.
 */

/** localStorage key for the sticky per-browser demo-mode toggle. */
export const DEMO_MODE_STORAGE_KEY = 'tsr.demoMode';

/** Test seam: force the flag on/off. `null` = fall back to real resolution. */
let override: boolean | null = null;

/** Test seam — pin demo-mode for a test, or `null` to restore real resolution. */
export function __setDemoModeOverride(value: boolean | null): void {
  override = value;
}

function truthyParam(value: string | null): boolean | null {
  if (value == null || value === '') return null;
  if (value === '0' || value === 'false' || value === 'off') return false;
  return true;
}

/**
 * Is demo-mode active? Resolved live so a test override (or a future in-session
 * toggle) takes effect immediately. Returns `false` outside a browser context.
 */
export function isDemoMode(): boolean {
  if (override !== null) return override;
  try {
    const fromQuery = truthyParam(new URLSearchParams(window.location.search).get('demo'));
    if (fromQuery !== null) return fromQuery;
    return window.localStorage.getItem(DEMO_MODE_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}
